/**
 * wipDedupe — planning the removal of duplicate iSyn_WIPs rows.
 *
 * 2,702 WIP numbers currently sit in that list more than once. Control Tower
 * and Subcon WIP Pending already de-duplicate before counting, so their
 * figures are right; every other reader of iSyn_WIPs is double-counting, and
 * at this scale nobody can fix it by hand.
 *
 * SAFE TO DELETE, REFERENTIALLY. Every child list — CutReadyScans,
 * ReceivingScans, Assignments, QcScans, StageHistory — keys on `wipId` as
 * TEXT, not as a lookup to the iSyn_WIPs row id. Nothing points at these rows
 * by item id, so removing a duplicate cannot orphan a scan or a history entry.
 * That is the risk this would otherwise carry, and it is not one here.
 *
 * WHICH ROW SURVIVES IS THE WHOLE QUESTION. The warning banner says "keep the
 * lowest id", which is a display convention, not a rule anybody verified: if
 * the older row is the emptier one — no batch, no due date, no measurements —
 * keeping it throws away the better record, 2,702 times, irreversibly. So the
 * keeper here is the MOST COMPLETE row, with the lowest id only as a
 * tie-break among equals.
 *
 * AND SOME GROUPS CANNOT BE RESOLVED THAT WAY AT ALL. When two rows each hold
 * a different non-empty value for the same field, one of them is wrong and
 * this module cannot know which. Those groups are marked `losesData` with the
 * exact fields named, sorted to the top of the report, and excluded from the
 * safe set — they are a question for a person, not a job for a batch delete.
 *
 * Nothing here deletes anything. It produces a plan and a report; the caller
 * decides.
 */

/** One iSyn_WIPs row, reduced to what the plan needs. The caller chooses
 * which fields to compare — a lookup's expanded object, for instance, is
 * better passed as its plain id than as a shape whose key order can vary. */
export interface IDedupeRow {
  /** SharePoint list item id. */
  id: number;
  /** The WIP number, which is what everything downstream actually keys on. */
  wipId: string;
  fields: Readonly<Record<string, unknown>>;
}

export interface IWipDuplicateGroup {
  wipId: string;
  /** The row to keep: most complete, lowest id among equals. */
  keep: IDedupeRow;
  /** The rows this plan would delete. */
  drop: IDedupeRow[];
  /**
   * A dropped row holds a value the keeper does not — either a field the
   * keeper leaves empty, or a DIFFERENT non-empty value for the same field.
   * Deleting would lose it.
   */
  losesData: boolean;
  /** The fields behind `losesData`, named so a person can judge them. */
  lostFields: string[];
}

/**
 * Columns that must never count as a disagreement.
 *
 * Two rows for the same WIP ALWAYS differ on these, so comparing them marks
 * every group as needing a person and the whole plan reads as unresolvable —
 * which is exactly what it did: 2,702 groups, none safe, on a report whose
 * entire purpose is to find the safe ones.
 *
 * `ID` is the one that bit. SharePoint's verbose OData returns a list item's
 * id under BOTH `Id` and `ID`, and only the first was being dropped — so the
 * second sailed into the comparison as an ordinary field and disagreed on
 * every single group, by definition. `Modified` was already excluded for the
 * identical reason, which is the whole argument for naming them in one place
 * rather than in a destructuring pattern where the next one gets missed.
 */
export const NOT_COMPARABLE: ReadonlySet<string> = new Set([
  // The item id, in both spellings SharePoint answers with.
  "Id", "ID",
  // Write timestamps: two rows written seconds apart differ, and neither is
  // more correct than the other.
  "Modified", "Created",
  // The expanded lookup object — its key order is not guaranteed, so two rows
  // agreeing on the batch could serialise differently. Its plain id
  // (BatchIDId) IS compared, and is the honest version of the same question.
  "BatchID",
  // OData envelope, never a field of the row.
  "__metadata", "GUID",
]);

/** A row's fields with the never-comparable ones removed. */
export function comparableFields(raw: Readonly<Record<string, unknown>>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(raw)) if (!NOT_COMPARABLE.has(k)) out[k] = v;
  return out;
}

const isEmpty = (v: unknown): boolean =>
  v === null || v === undefined || (typeof v === "string" && v.trim() === "");

/** Compared as text, so 4 and "4" are the same value — SharePoint returns
 * numeric columns either way depending on how the row was written. */
function norm(v: unknown): string {
  if (isEmpty(v)) return "";
  if (typeof v === "string") return v.trim();
  return String(v);
}

/** How much a row actually carries. The keeper is the row with the most of
 * it — the one measure that cannot throw away a record in favour of a stub. */
export function completeness(row: IDedupeRow): number {
  let n = 0;
  for (const v of Object.values(row.fields)) if (!isEmpty(v)) n++;
  return n;
}

/**
 * Groups duplicate rows and picks a survivor for each.
 *
 * Rows with a blank WIP number are skipped entirely: they cannot be grouped
 * by the thing everything downstream keys on, and a batch delete is no place
 * to be guessing at what an unlabelled row was for.
 */
export function planWipDedupe(rows: readonly IDedupeRow[]): IWipDuplicateGroup[] {
  const byWip = new Map<string, IDedupeRow[]>();
  for (const r of rows) {
    const key = (r.wipId ?? "").trim();
    if (!key) continue;
    const list = byWip.get(key);
    if (list) list.push(r); else byWip.set(key, [r]);
  }

  const out: IWipDuplicateGroup[] = [];
  for (const [wipId, group] of byWip) {
    if (group.length < 2) continue;
    const ranked = [...group].sort(
      (a, b) => completeness(b) - completeness(a) || a.id - b.id);
    const [keep, ...drop] = ranked;

    const lost = new Set<string>();
    for (const d of drop) {
      for (const [field, value] of Object.entries(d.fields)) {
        if (isEmpty(value)) continue;
        if (norm(keep.fields[field]) !== norm(value)) lost.add(field);
      }
    }
    out.push({
      wipId, keep, drop,
      losesData: lost.size > 0,
      lostFields: [...lost].sort(),
    });
  }

  // Lossy groups first: they are the ones needing a decision, and a report
  // that opens with the safe majority buries them.
  return out.sort(
    (a, b) => Number(b.losesData) - Number(a.losesData) || a.wipId.localeCompare(b.wipId));
}

/** The groups a batch delete may act on — every dropped row is redundant. */
export function safeGroups(groups: readonly IWipDuplicateGroup[]): IWipDuplicateGroup[] {
  return groups.filter(g => !g.losesData);
}

/** Every row id the safe groups would delete. The ONLY list a delete should
 * ever be handed: derived here rather than re-computed by the caller, so what
 * is reviewed and what is deleted cannot drift apart. */
export function idsToDelete(groups: readonly IWipDuplicateGroup[]): number[] {
  return safeGroups(groups).flatMap(g => g.drop.map(r => r.id)).sort((a, b) => a - b);
}

export interface IDedupeSummary {
  duplicatedWips: number;
  extraRows: number;
  safeWips: number;
  safeRows: number;
  needsReviewWips: number;
  needsReviewRows: number;
}

export function dedupeSummary(groups: readonly IWipDuplicateGroup[]): IDedupeSummary {
  const s: IDedupeSummary = {
    duplicatedWips: groups.length, extraRows: 0,
    safeWips: 0, safeRows: 0, needsReviewWips: 0, needsReviewRows: 0,
  };
  for (const g of groups) {
    s.extraRows += g.drop.length;
    if (g.losesData) { s.needsReviewWips++; s.needsReviewRows += g.drop.length; }
    else { s.safeWips++; s.safeRows += g.drop.length; }
  }
  return s;
}

const csvField = (v: string | number): string => {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * The dry run, as a file.
 *
 * One row per duplicate group, saying exactly which item id survives, which
 * are deleted, and — where it matters — what would be lost. This is the thing
 * that gets read BEFORE anything is deleted, so it states its own verdict per
 * row rather than leaving the reader to infer it from ids.
 */
export function dedupeReportCsv(groups: readonly IWipDuplicateGroup[], generatedAt: string): string {
  const s = dedupeSummary(groups);
  const lines = [
    "# iSyn_WIPs duplicate rows — dry run, nothing has been deleted",
    `# generated,${csvField(generatedAt)}`,
    `# WIP numbers duplicated,${s.duplicatedWips},extra rows,${s.extraRows}`,
    `# safe to delete,${s.safeRows} rows across ${s.safeWips} WIPs`,
    `# needs a person,${s.needsReviewRows} rows across ${s.needsReviewWips} WIPs`,
    "# keeper = most complete row, lowest item id among equals",
    ["WIP", "VERDICT", "KEEP ID", "DELETE IDS", "ROWS", "WOULD LOSE"].join(","),
  ];
  for (const g of groups) {
    lines.push([
      g.wipId,
      g.losesData ? "NEEDS REVIEW" : "SAFE",
      g.keep.id,
      g.drop.map(r => r.id).join(" "),
      g.drop.length + 1,
      g.lostFields.join(" "),
    ].map(csvField).join(","));
  }
  return lines.join("\r\n") + "\r\n";
}
